import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  useTheme,
  Button,
  Chip,
  Avatar,
  Stack,
  Card,
  CardContent,
  alpha,
  TextField,
  Autocomplete
} from '@mui/material';
import { Wrapper } from '@googlemaps/react-wrapper';
import {
  Engineering,
  Refresh,
  Circle,
  CheckCircle,
  Warning,
  Map as MapIcon,
  FilterList,
  Search,
  People
} from '@mui/icons-material';
import { configurationService } from '../../services/configurationService';
import { monitoramentoService } from '../../services/monitoramentoService';
import { IFuncionario } from '../../types';

const validateCoordinates = (coordString: any): { lat: number; lng: number } | null => {
  if (!coordString || typeof coordString !== 'string') return null;
  const parts = coordString.split(',');
  if (parts.length !== 2) return null;

  const lat = parseFloat(parts[0].trim());
  const lng = parseFloat(parts[1].trim());

  if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
    return { lat, lng };
  }
  return null;
};

const STATUS_COLORS = {
  ativo: '#7B1FA2',
  inativo: '#9E9E9E'
} as const;

const FILTER_OPTIONS = [
  { key: 'Todos', label: 'Todos', color: undefined },
  { key: 'Ativo', label: 'Ativos', color: STATUS_COLORS.ativo },
  { key: 'Inativo', label: 'Inativos', color: STATUS_COLORS.inativo }
] as const;

const getTecnicoMarkerColor = (status: IFuncionario['status']) =>
  status === 'ativo' ? STATUS_COLORS.ativo : STATUS_COLORS.inativo;

const getStatusLabel = (status: IFuncionario['status']) => {
  switch (status) {
    case 'ativo':
      return 'Ativo';
    case 'inativo':
      return 'Inativo';
    case 'demitido':
      return 'Demitido';
    case 'aposentado':
      return 'Aposentado';
    default:
      return status;
  }
};

const matchesFilter = (tecnico: IFuncionario, filter: string) => {
  if (filter === 'Todos') return true;
  if (filter === 'Ativo') return tecnico.status === 'ativo';
  if (filter === 'Inativo') return tecnico.status !== 'ativo';
  return true;
};

const darkMapStyles: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#1f2933' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#d5dbe3' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#111827' }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#4b5563' }] },
  { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#18212b' }] },
  { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#243241' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#173524' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#354252' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#111827' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#4b5563' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#1f2937' }] },
  { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#263445' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0f2537' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#9fb3c8' }] }
];

interface StatCardProps {
  label: string;
  value: number;
  color?: string;
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, color, icon, active, onClick }) => (
  <Card
    elevation={active ? 4 : 0}
    onClick={onClick}
    sx={{
      cursor: 'pointer',
      height: '100%',
      border: '2px solid',
      borderColor: active ? (color || 'primary.main') : 'divider',
      bgcolor: active ? alpha(color || '#2196F3', 0.08) : 'background.paper',
      transition: 'all 0.2s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: 3,
        borderColor: color || 'primary.main'
      }
    }}
  >
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
            {label}
          </Typography>
          <Typography variant="h4" fontWeight={800} sx={{ color: color || 'primary.main', lineHeight: 1.2, mt: 0.5 }}>
            {value}
          </Typography>
        </Box>
        <Avatar
          sx={{
            width: 44,
            height: 44,
            bgcolor: alpha(color || '#2196F3', 0.12),
            color: color || 'primary.main'
          }}
        >
          {icon}
        </Avatar>
      </Stack>
    </CardContent>
  </Card>
);

const createMarkerIcon = (color: string, highlighted = false): google.maps.Symbol => ({
  path: window.google.maps.SymbolPath.CIRCLE,
  fillColor: color,
  fillOpacity: highlighted ? 1 : 0.92,
  strokeColor: '#FFFFFF',
  strokeWeight: highlighted ? 3.5 : 2.5,
  scale: highlighted ? 14 : 9
});

interface MapComponentProps {
  tecnicos: IFuncionario[];
  isDarkMode: boolean;
  focusTarget?: {
    lat: number;
    lng: number;
    tecnicoId: number;
    name: string;
    markerColor: string;
    token: number;
  } | null;
}

const MapComponent: React.FC<MapComponentProps> = ({ tecnicos, isDarkMode, focusTarget }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersDataRef = useRef<Map<number, { marker: google.maps.Marker; tecnico: IFuncionario; color: string }>>(new Map());
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const highlightRingRef = useRef<google.maps.Marker | null>(null);
  const highlightPulseRef = useRef<number | null>(null);
  const highlightBounceRef = useRef<number | null>(null);
  const highlightInfoWindowRef = useRef<google.maps.InfoWindow | null>(null);

  const [center] = useState({ lat: -15.7801, lng: -47.9292 });
  const [zoom] = useState(5);

  const clearHighlightEffects = useCallback(() => {
    highlightRingRef.current?.setMap(null);
    highlightRingRef.current = null;

    if (highlightPulseRef.current) {
      window.clearInterval(highlightPulseRef.current);
      highlightPulseRef.current = null;
    }

    if (highlightBounceRef.current) {
      window.clearTimeout(highlightBounceRef.current);
      highlightBounceRef.current = null;
    }

    highlightInfoWindowRef.current?.close();
    highlightInfoWindowRef.current = null;

    if (window.google?.maps) {
      markersDataRef.current.forEach(({ marker, color }) => {
        marker.setAnimation(null);
        marker.setZIndex(undefined);
        marker.setIcon(createMarkerIcon(color));
      });
    }
  }, []);

  const applyHighlight = useCallback((target: NonNullable<MapComponentProps['focusTarget']>) => {
    if (!mapInstanceRef.current || !window.google?.maps) return;

    const map = mapInstanceRef.current;
    const entry = markersDataRef.current.get(target.tecnicoId);
    if (!entry) return;

    const { marker } = entry;

    marker.setIcon(createMarkerIcon(target.markerColor, true));
    marker.setZIndex(window.google.maps.Marker.MAX_ZINDEX + 1);
    marker.setAnimation(window.google.maps.Animation.BOUNCE);

    highlightBounceRef.current = window.setTimeout(() => {
      marker.setAnimation(null);
    }, 2800);

    let pulseScale = 20;
    let growing = true;
    const ringMarker = new window.google.maps.Marker({
      position: { lat: target.lat, lng: target.lng },
      map,
      clickable: false,
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        fillColor: target.markerColor,
        fillOpacity: 0.18,
        strokeColor: target.markerColor,
        strokeWeight: 2.5,
        strokeOpacity: 0.7,
        scale: pulseScale
      },
      zIndex: window.google.maps.Marker.MAX_ZINDEX
    });
    highlightRingRef.current = ringMarker;

    highlightPulseRef.current = window.setInterval(() => {
      if (growing) {
        pulseScale += 1.2;
        if (pulseScale >= 32) growing = false;
      } else {
        pulseScale -= 1.2;
        if (pulseScale <= 20) growing = true;
      }

      ringMarker.setIcon({
        path: window.google.maps.SymbolPath.CIRCLE,
        fillColor: target.markerColor,
        fillOpacity: 0.1 + (pulseScale - 20) * 0.008,
        strokeColor: target.markerColor,
        strokeWeight: 2.5,
        strokeOpacity: 0.45 + (32 - pulseScale) * 0.015,
        scale: pulseScale
      });
    }, 90);

    const infoWindow = new window.google.maps.InfoWindow({
      content: `
        <div style="padding:2px 4px;text-align:center;">
          <div style="font-weight:800;font-size:13px;color:#111827;">${target.name}</div>
          <div style="font-size:11px;color:#6b7280;margin-top:2px;">Tecnico selecionado</div>
        </div>
      `
    });
    infoWindow.open(map, marker);
    highlightInfoWindowRef.current = infoWindow;
  }, []);

  useEffect(() => {
    if (!mapRef.current || !window.google?.maps) return;

    const map = new window.google.maps.Map(mapRef.current, {
      center,
      zoom,
      mapTypeId: 'roadmap',
      mapTypeControl: true,
      streetViewControl: true,
      fullscreenControl: true,
      backgroundColor: isDarkMode ? '#111827' : '#ffffff',
      styles: isDarkMode ? darkMapStyles : []
    });

    mapInstanceRef.current = map;
    infoWindowRef.current = new window.google.maps.InfoWindow();

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        map.setCenter({ lat: position.coords.latitude, lng: position.coords.longitude });
        map.setZoom(13);
      });
    }

    return () => {
      clearHighlightEffects();
      markersDataRef.current.forEach(({ marker }) => marker.setMap(null));
      markersDataRef.current.clear();
    };
  }, [center, zoom, clearHighlightEffects]);

  useEffect(() => {
    if (!mapInstanceRef.current) return;

    mapInstanceRef.current.setOptions({
      backgroundColor: isDarkMode ? '#111827' : '#ffffff',
      styles: isDarkMode ? darkMapStyles : []
    });
  }, [isDarkMode]);

  useEffect(() => {
    if (!mapInstanceRef.current || !window.google?.maps) return;

    const map = mapInstanceRef.current;

    clearHighlightEffects();
    markersDataRef.current.forEach(({ marker }) => marker.setMap(null));
    markersDataRef.current.clear();

    const bounds = new window.google.maps.LatLngBounds();
    let hasValidPoints = false;

    tecnicos.forEach(tecnico => {
      const coords = validateCoordinates(tecnico.coordenadas);
      if (coords) {
        const markerColor = getTecnicoMarkerColor(tecnico.status);

        const marker = new window.google.maps.Marker({
          position: coords,
          map,
          title: tecnico.nome,
          icon: createMarkerIcon(markerColor)
        });

        marker.addListener('click', () => {
          const content = `
            <div style="padding: 8px;">
              <strong>Tecnico:</strong> ${tecnico.nome}<br/>
              <strong>Cargo:</strong> ${tecnico.cargo || 'N/A'}<br/>
              <strong>Status:</strong> ${getStatusLabel(tecnico.status)}<br/>
              <strong>Celular:</strong> ${tecnico.celular || 'N/A'}
            </div>
          `;
          infoWindowRef.current?.setContent(content);
          infoWindowRef.current?.open(map, marker);
        });

        markersDataRef.current.set(tecnico.id, { marker, tecnico, color: markerColor });
        bounds.extend(coords);
        hasValidPoints = true;
      }
    });

    if (hasValidPoints && !focusTarget) {
      map.fitBounds(bounds);
      const listener = window.google.maps.event.addListener(map, 'idle', function() {
        if (map.getZoom()! > 18) map.setZoom(18);
        window.google.maps.event.removeListener(listener);
      });
    }
  }, [tecnicos, focusTarget, clearHighlightEffects]);

  useEffect(() => {
    if (!mapInstanceRef.current || !window.google?.maps) return;

    clearHighlightEffects();

    if (!focusTarget) return;

    const map = mapInstanceRef.current;
    map.panTo({ lat: focusTarget.lat, lng: focusTarget.lng });
    map.setZoom(17);
    applyHighlight(focusTarget);

    return () => clearHighlightEffects();
  }, [focusTarget, tecnicos, applyHighlight, clearHighlightEffects]);

  return <Box ref={mapRef} sx={{ width: '100%', height: '100%' }} />;
};

const MonitoramentoTecnicosPage: React.FC = () => {
  const theme = useTheme();
  const primario = theme.palette.primary.main;
  const [googleMapsApiKey, setGoogleMapsApiKey] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeFilter, setActiveFilter] = useState<string>('Todos');
  const [tecnicos, setTecnicos] = useState<IFuncionario[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [focusTarget, setFocusTarget] = useState<{
    lat: number;
    lng: number;
    tecnicoId: number;
    name: string;
    markerColor: string;
    token: number;
  } | null>(null);

  const tecnicosWithCoords = useMemo(
    () => tecnicos.filter(t => validateCoordinates(t.coordenadas)),
    [tecnicos]
  );

  const stats = useMemo(() => ({
    total: tecnicosWithCoords.length,
    ativo: tecnicosWithCoords.filter(t => matchesFilter(t, 'Ativo')).length,
    inativo: tecnicosWithCoords.filter(t => matchesFilter(t, 'Inativo')).length
  }), [tecnicosWithCoords]);

  const filteredTecnicos = useMemo(
    () => tecnicosWithCoords.filter(t => matchesFilter(t, activeFilter)),
    [tecnicosWithCoords, activeFilter]
  );

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const config = await configurationService.getConfigurationByKey('google_maps_api_key');
      if (config && config.valor) {
        setGoogleMapsApiKey(config.valor);
      } else {
        setError('Chave da API do Google Maps nao configurada no sistema.');
        return;
      }

      const data = await monitoramentoService.getTecnicos();
      setTecnicos(data || []);
    } catch (err: any) {
      console.error('Erro ao inicializar mapa:', err);
      setError('Erro ao carregar dados do mapa. ' + (err.message || ''));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleFilterChange = useCallback((filter: string) => {
    setActiveFilter(filter);
    setFocusTarget(null);
  }, []);

  const handleSearchSelect = useCallback((tecnico: IFuncionario | null) => {
    if (!tecnico) return;

    const coords = validateCoordinates(tecnico.coordenadas);
    if (!coords) return;

    const isVisible = filteredTecnicos.some(t => t.id === tecnico.id);
    if (!isVisible) {
      setActiveFilter('Todos');
    }

    setFocusTarget({
      lat: coords.lat,
      lng: coords.lng,
      tecnicoId: tecnico.id,
      name: tecnico.nome,
      markerColor: getTecnicoMarkerColor(tecnico.status),
      token: Date.now()
    });
    setSearchInput('');
  }, [filteredTecnicos]);

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 2 }}>
        <CircularProgress size={48} thickness={4} />
        <Typography color="text.secondary" fontWeight={500}>Carregando mapa de tecnicos...</Typography>
      </Box>
    );
  }

  if (error || !googleMapsApiKey) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            textAlign: 'center',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Avatar sx={{ width: 56, height: 56, mx: 'auto', mb: 2, bgcolor: alpha(theme.palette.error.main, 0.1), color: 'error.main' }}>
            <MapIcon />
          </Avatar>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>Monitoramento de Tecnicos</Typography>
          <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>{error || 'Chave do Google Maps invalida.'}</Alert>
          <Button variant="contained" onClick={loadData} startIcon={<Refresh />}>Tentar Novamente</Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0.5, height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper
        elevation={2}
        sx={{
          mb: 0,
          borderRadius: 2,
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${alpha(primario, 0.04)} 0%, ${alpha(theme.palette.secondary.main, 0.04)} 100%)`,
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: alpha(primario, 0.12), color: primario, width: 40, height: 40 }}>
              <Engineering sx={{ fontSize: 22 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={800} sx={{ fontSize: '1.05rem', lineHeight: 1.2 }}>
                Monitoramento de Tecnicos
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                Acompanhe a localizacao dos tecnicos em campo em tempo real
              </Typography>
            </Box>
          </Box>
          <Box
            display="flex"
            gap={0.5}
            alignItems="center"
            sx={{ width: { xs: '100%', md: 'auto' }, minWidth: { md: 420 }, flex: { xs: 1, md: 'unset' } }}
          >
            <Autocomplete
              size="small"
              options={tecnicosWithCoords}
              getOptionLabel={(option) => option.nome}
              inputValue={searchInput}
              onInputChange={(_, value) => setSearchInput(value)}
              onChange={(_, value) => handleSearchSelect(value)}
              filterOptions={(options, { inputValue }) =>
                options.filter(option =>
                  option.nome.toLowerCase().includes(inputValue.trim().toLowerCase()) ||
                  (option.cargo || '').toLowerCase().includes(inputValue.trim().toLowerCase())
                )
              }
              noOptionsText="Nenhum tecnico encontrado"
              sx={{ flex: 1, minWidth: 0 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Pesquisar tecnico pelo nome..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <Search sx={{ ml: 0.5, mr: 0.5, color: 'action.active', fontSize: 18 }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    bgcolor: 'background.paper',
                    '& .MuiOutlinedInput-root': { height: 32 },
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.25 }}>
                    <Circle sx={{ fontSize: 10, color: getTecnicoMarkerColor(option.status) }} />
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{option.nome}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.cargo} · {getStatusLabel(option.status)}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              )}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={loadData}
              startIcon={<Refresh sx={{ fontSize: 18 }} />}
              disabled={loading}
              sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            >
              Atualizar Mapa
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Stats Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
          gap: 2
        }}
      >
        <StatCard
          label="Total no mapa"
          value={stats.total}
          icon={<People />}
          active={activeFilter === 'Todos'}
          onClick={() => handleFilterChange('Todos')}
        />
        <StatCard
          label="Ativos"
          value={stats.ativo}
          color={STATUS_COLORS.ativo}
          icon={<CheckCircle />}
          active={activeFilter === 'Ativo'}
          onClick={() => handleFilterChange('Ativo')}
        />
        <StatCard
          label="Inativos"
          value={stats.inativo}
          color={STATUS_COLORS.inativo}
          icon={<Warning />}
          active={activeFilter === 'Inativo'}
          onClick={() => handleFilterChange('Inativo')}
        />
      </Box>

      {/* Map */}
      <Paper
        elevation={3}
        sx={{
          flex: 1,
          minHeight: 520,
          overflow: 'hidden',
          borderRadius: 3,
          position: 'relative',
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            pointerEvents: 'none'
          }}
        >
          <Paper
            elevation={4}
            sx={{
              px: 1.5,
              py: 1,
              borderRadius: 2,
              pointerEvents: 'auto',
              backdropFilter: 'blur(8px)',
              bgcolor: alpha(theme.palette.background.paper, 0.92)
            }}
          >
            <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="center" sx={{ mb: 0.75 }}>
              <FilterList fontSize="small" color="action" />
              <Typography variant="caption" fontWeight={700} color="text.secondary">
                Filtro: {FILTER_OPTIONS.find(f => f.key === activeFilter)?.label}
              </Typography>
              <Chip label={filteredTecnicos.length} size="small" color="primary" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }} />
            </Stack>
            <Stack direction="row" spacing={1.5} justifyContent="center" flexWrap="wrap" useFlexGap>
              {FILTER_OPTIONS.filter(f => f.key !== 'Todos').map(f => (
                <Stack key={f.key} direction="row" spacing={0.5} alignItems="center">
                  <Circle sx={{ fontSize: 10, color: f.color }} />
                  <Typography variant="caption" color="text.secondary">{f.label}</Typography>
                </Stack>
              ))}
            </Stack>
          </Paper>
        </Box>
        <Wrapper apiKey={googleMapsApiKey}>
          <MapComponent
            tecnicos={filteredTecnicos}
            isDarkMode={theme.palette.mode === 'dark'}
            focusTarget={focusTarget}
          />
        </Wrapper>
      </Paper>
    </Box>
  );
};

export default MonitoramentoTecnicosPage;
